import { useMemo } from "react";
import { TradeToEarnCampaignStatus } from "@cede/types";
import { useCampaigns } from "@/hooks/campaign/useCampaigns";
import { useHubUser } from "@/hooks/user/useHubUser";

/**
 * Hook to fetch exclusive campaigns where the current user is whitelisted.
 *
 * This filters the existing campaign data - no additional API calls needed
 * since `isParticipantWhitelisted` is already returned by the authenticated endpoint.
 *
 * Only returns:
 * - Non-past campaigns (LIVE/FUTURE/PAUSED)
 * - Campaigns where user is whitelisted (isParticipantWhitelisted === true)
 */
export const useWhitelistedCampaigns = () => {
  const { authenticated } = useHubUser();

  const { campaigns, isLoading } = useCampaigns({
    status: [TradeToEarnCampaignStatus.LIVE, TradeToEarnCampaignStatus.FUTURE, TradeToEarnCampaignStatus.PAUSED],
    enabled: authenticated, // Only fetch when user is authenticated
    skipUserStats: true, // We don't need participation stats for notifications
  });

  const whitelistedExclusiveCampaigns = useMemo(() => {
    if (!campaigns || !authenticated) return [];

    return campaigns.filter((campaign) => campaign.isParticipantWhitelisted === true);
  }, [campaigns, authenticated]);

  return {
    whitelistedCampaigns: whitelistedExclusiveCampaigns,
    isLoading,
    hasWhitelistedCampaigns: whitelistedExclusiveCampaigns.length > 0,
    count: whitelistedExclusiveCampaigns.length,
  };
};
